import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  ControlArchiveAttachment,
  ControlArchivePost,
  ControlArchivePostDetail,
  controlArchiveService,
} from '../services/controlArchiveService';
import { formatDateTimeKst } from '../utils/dateUtils';
import FileTransferProgressDialog, {
  FileTransferMode,
} from '../components/common/FileTransferProgressDialog';

export type ControlArchivePanelProps = {
  embedded?: boolean;
};

const emptyForm = {
  title: '',
  body: '',
  files: [] as File[],
};

export const ControlArchivePanel: React.FC<ControlArchivePanelProps> = ({
  embedded = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<ControlArchivePost[]>([]);
  const [allowDelete, setAllowDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ControlArchivePost | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [existingAttachments, setExistingAttachments] = useState<ControlArchiveAttachment[]>(
    []
  );
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<number[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<ControlArchivePostDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferMode, setTransferMode] = useState<FileTransferMode>('upload');
  const [transferOverallPercent, setTransferOverallPercent] = useState<number | null>(
    null
  );
  const [transferFilePercent, setTransferFilePercent] = useState<number | null>(null);
  const [transferName, setTransferName] = useState<string | null>(null);
  const [transferFileIndex, setTransferFileIndex] = useState<number | null>(null);
  const [transferFileCount, setTransferFileCount] = useState<number | null>(null);
  const [transferMessage, setTransferMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const savingRef = useRef(false);

  const resetTransferUi = () => {
    setTransferOpen(false);
    setTransferOverallPercent(null);
    setTransferFilePercent(null);
    setTransferName(null);
    setTransferFileIndex(null);
    setTransferFileCount(null);
    setTransferMessage(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await controlArchiveService.list();
      setItems(res.items || []);
      setAllowDelete(Boolean(res.allow_delete));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '목록을 불러오기 실패', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setFileInputKey((k) => k + 1);
    setDialogOpen(true);
  };

  const openEdit = async (row: ControlArchivePost, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(row);
    setForm({
      title: row.title,
      body: row.body,
      files: [],
    });
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
    setFileInputKey((k) => k + 1);
    setDialogOpen(true);
    try {
      const d = await controlArchiveService.getDetail(row.id);
      setExistingAttachments(d.attachments || []);
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || '첨부 목록을 불러오기 실패', {
        variant: 'error',
      });
    }
  };

  const openDetail = async (row: ControlArchivePost) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await controlArchiveService.getDetail(row.id);
      setDetail(d);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '상세를 불러오기 실패', {
        variant: 'error',
      });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const keptAttachments = existingAttachments.filter(
    (a) => !removedAttachmentIds.includes(a.id)
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    setForm((f) => {
      const room = Math.max(0, 5 - keptAttachments.length - f.files.length);
      return { ...f, files: [...f.files, ...picked].slice(0, f.files.length + room) };
    });
    e.target.value = '';
  };

  const save = async () => {
    if (savingRef.current) return;
    if (!form.title.trim() || !form.body.trim()) {
      enqueueSnackbar('제목과 내용을 입력', { variant: 'warning' });
      return;
    }
    if (keptAttachments.length + form.files.length > 5) {
      enqueueSnackbar('첨부는 최대 5개까지 가능', { variant: 'warning' });
      return;
    }
    const removeIds = removedAttachmentIds.slice();
    if (removeIds.length > 0 && !allowDelete) {
      enqueueSnackbar(
        '첨부 삭제가 허용되지 않음. 시스템관리에서 「삭제 허용」을 켠 뒤 다시 시도.',
        { variant: 'warning' }
      );
      return;
    }
    const filesSnapshot = form.files.slice(0, Math.max(0, 5 - keptAttachments.length));
    const hasFiles = filesSnapshot.length > 0;
    savingRef.current = true;
    setSaving(true);
    if (hasFiles) {
      setForm((f) => ({ ...f, files: [] }));
      setFileInputKey((k) => k + 1);
    }
    try {
      if (editing) {
        const postId = editing.id;
        await controlArchiveService.update(postId, {
          title: form.title,
          body: form.body,
        });
        for (const aid of removeIds) {
          await controlArchiveService.deleteAttachment(aid);
        }
        if (hasFiles) {
          setTransferMode('upload');
          setTransferOverallPercent(0);
          setTransferFilePercent(0);
          setTransferFileIndex(1);
          setTransferFileCount(filesSnapshot.length);
          setTransferName(filesSnapshot[0]?.name || null);
          setTransferMessage(null);
          setTransferOpen(true);
          setDialogOpen(false);
          await controlArchiveService.addAttachments(postId, filesSnapshot, {
            onUploadFileProgress: (info) => {
              setTransferOverallPercent(info.overallPercent);
              setTransferFilePercent(info.filePercent);
              setTransferFileIndex(info.fileIndex);
              setTransferFileCount(info.fileCount);
              setTransferName(info.currentFilename || null);
            },
          });
        } else {
          setDialogOpen(false);
        }
        enqueueSnackbar('수정됨', { variant: 'success' });
        setForm({ ...emptyForm });
        setEditing(null);
        setExistingAttachments([]);
        setRemovedAttachmentIds([]);
      } else {
        if (hasFiles) {
          setTransferMode('upload');
          setTransferOverallPercent(0);
          setTransferFilePercent(0);
          setTransferFileIndex(1);
          setTransferFileCount(filesSnapshot.length);
          setTransferName(filesSnapshot[0]?.name || null);
          setTransferMessage(null);
          setTransferOpen(true);
          setDialogOpen(false);
        }
        await controlArchiveService.create({
          title: form.title,
          body: form.body,
          files: filesSnapshot,
          onUploadFileProgress: hasFiles
            ? (info) => {
                setTransferOverallPercent(info.overallPercent);
                setTransferFilePercent(info.filePercent);
                setTransferFileIndex(info.fileIndex);
                setTransferFileCount(info.fileCount);
                setTransferName(info.currentFilename || null);
              }
            : undefined,
        });
        enqueueSnackbar('등록 완료', { variant: 'success' });
        setForm({ ...emptyForm });
        setEditing(null);
        if (!hasFiles) {
          setDialogOpen(false);
        }
      }
      await load();
    } catch (e: any) {
      const detailMsg = e?.response?.data?.detail;
      const status = e?.response?.status;
      let msg = '저장에 실패';
      if (typeof detailMsg === 'string' && detailMsg.trim()) msg = detailMsg;
      else if (e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message || ''))) {
        msg = '업로드 시간이 초과됨. 네트워크가 느리거나 파일이 매우 큼.';
      } else if (!e?.response && /network error/i.test(String(e?.message || ''))) {
        msg =
          '연결이 끊김. 목록에 글·첨부가 있는지 먼저 확인. 없으면 다시 등록.';
      } else if (status === 413) {
        msg = '파일이 너무 큼. (서버/프록시 요청 크기 제한)';
      } else if (status === 401 || status === 403) {
        msg = '로그인이 만료. 다시 로그인한 뒤 등록';
      } else if (status === 500 || status === 502 || status === 503 || status === 504) {
        msg =
          '서버 응답 오류. 목록에 이미 등록됐는지 확인한 뒤, 없을 때만 다시 등록.';
      } else if (status) {
        msg = `저장에 실패. (HTTP ${status})`;
      }
      let alreadySaved = false;
      if (hasFiles && !editing) {
        try {
          const list = await controlArchiveService.list();
          alreadySaved = (list.items || []).some(
            (it) =>
              it.title === form.title.trim() &&
              (it.attachment_count || 0) >= filesSnapshot.length
          );
        } catch {
          /* ignore */
        }
      }
      if (alreadySaved) {
        enqueueSnackbar('등록이 완료된 것으로 보. 목록을 확인', {
          variant: 'success',
        });
        setForm({ ...emptyForm });
        setEditing(null);
        setDialogOpen(false);
        await load();
      } else {
        enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 8000 });
        if (hasFiles) {
          setForm((f) => ({
            ...f,
            files: filesSnapshot,
            title: f.title || form.title,
            body: f.body || form.body,
          }));
          setDialogOpen(true);
        }
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      resetTransferUi();
    }
  };

  const handleDownloadAll = async (post: ControlArchivePostDetail) => {
    const attachments = post.attachments || [];
    if (attachments.length === 0) return;
    setTransferMode('download');
    setTransferOverallPercent(0);
    setTransferFilePercent(0);
    setTransferName(attachments[0]?.original_name || null);
    setTransferFileIndex(1);
    setTransferFileCount(attachments.length);
    setTransferOpen(true);
    try {
      await controlArchiveService.downloadAllAttachments(attachments, (info) => {
        setTransferOverallPercent(info.overallPercent);
        setTransferFilePercent(info.filePercent);
        setTransferFileIndex(info.fileIndex);
        setTransferFileCount(info.fileCount);
        setTransferName(info.currentFilename || null);
      });
      enqueueSnackbar(`${attachments.length}개 파일 수신`, { variant: 'success' });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '일괄 다운로드에 실패', {
        variant: 'error',
      });
    } finally {
      resetTransferUi();
    }
  };

  const handleDownload = async (attachmentId: number, filename: string) => {
    setTransferMode('download');
    setTransferOverallPercent(0);
    setTransferFilePercent(0);
    setTransferName(filename);
    setTransferFileIndex(1);
    setTransferFileCount(1);
    setTransferOpen(true);
    try {
      await controlArchiveService.downloadAttachment(attachmentId, filename, (p) => {
        setTransferOverallPercent(p);
        setTransferFilePercent(p);
      });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail || '다운로드에 실패', {
        variant: 'error',
      });
    } finally {
      resetTransferUi();
    }
  };

  const removePost = async (row: ControlArchivePost, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!(row.can_delete ?? allowDelete)) {
      enqueueSnackbar(
        '삭제가 허용되지 않음. 시스템관리 → 데이터 관리 → 관제 자료실에서 「삭제 허용」을 켠 뒤 다시 시도.',
        { variant: 'warning' }
      );
      return;
    }
    if (!window.confirm(`「${row.title}」글 삭제 확인`)) return;
    try {
      await controlArchiveService.remove(row.id);
      enqueueSnackbar('삭제 완료', { variant: 'success' });
      if (detail?.id === row.id) {
        setDetailOpen(false);
        setDetail(null);
      }
      await load();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || '삭제에 실패', {
        variant: 'error',
      });
    }
  };

  const content = (
    <Box>
      <FileTransferProgressDialog
        open={transferOpen}
        mode={transferMode}
        message={transferMessage}
        currentFilename={transferName}
        filePercent={transferFilePercent}
        overallPercent={transferOverallPercent}
        fileIndex={transferFileIndex}
        fileCount={transferFileCount}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">관제 자료실</Typography>
          <Typography variant="body2" color="text.secondary">
            중앙관제 내부 자료 공유 (시설 발신·수신 없음)
            {!allowDelete
              ? ' · 삭제 잠금(시스템관리에서 「삭제 허용」 필요)'
              : ' · 삭제 허용 중'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            작성
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>제목</TableCell>
              <TableCell width={160}>작성</TableCell>
              <TableCell width={80}>첨부</TableCell>
              <TableCell width={160}>일시</TableCell>
              <TableCell width={96} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {loading ? '불러오는 중…' : '자료 없음'}
                </TableCell>
              </TableRow>
            )}
            {items.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => openDetail(row)}
              >
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.author_label}</TableCell>
                <TableCell>{row.attachment_count > 0 ? row.attachment_count : '-'}</TableCell>
                <TableCell>{formatDateTimeKst(row.created_at)}</TableCell>
                <TableCell align="right" onClick={(ev) => ev.stopPropagation()}>
                  {row.can_edit && (
                    <Tooltip title="수정">
                      <IconButton size="small" onClick={(ev) => openEdit(row, ev)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {row.can_delete && (
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(ev) => removePost(row, ev)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '자료 수정' : '자료 등록'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="제목"
              fullWidth
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              label="내용"
              fullWidth
              multiline
              minRows={4}
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
            <Box>
              {editing && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    기존 첨부 (×로 삭제, 최대 5개)
                  </Typography>
                  {keptAttachments.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      없음
                    </Typography>
                  ) : (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {keptAttachments.map((a) => (
                        <Chip
                          key={a.id}
                          size="small"
                          icon={<AttachFileIcon />}
                          label={a.original_name}
                          onDelete={
                            allowDelete
                              ? () =>
                                  setRemovedAttachmentIds((ids) =>
                                    ids.includes(a.id) ? ids : [...ids, a.id]
                                  )
                              : undefined
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              <Button
                variant="outlined"
                component="label"
                startIcon={<AttachFileIcon />}
                disabled={keptAttachments.length + form.files.length >= 5}
              >
                {editing ? '첨부 추가' : '첨부'} (최대 5개
                {editing ? `, 남은 ${Math.max(0, 5 - keptAttachments.length - form.files.length)}` : ''}
                )
                <input
                  key={fileInputKey}
                  type="file"
                  hidden
                  multiple
                  onChange={onFileChange}
                />
              </Button>
              {form.files.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                  {form.files.map((f, idx) => (
                    <Chip
                      key={`${f.name}-${idx}`}
                      size="small"
                      label={f.name}
                      onDelete={() =>
                        setForm((prev) => ({
                          ...prev,
                          files: prev.files.filter((_, i) => i !== idx),
                        }))
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            취소
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? (editing ? '수정 중…' : '등록 중…') : editing ? '수정' : '등록'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{detail?.title || '상세'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <Typography>불러오는 중…</Typography>}
          {detail && !detailLoading && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                작성: {detail.author_label} · {formatDateTimeKst(detail.created_at)}
              </Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.body}</Typography>
              {detail.attachments?.length > 0 && (
                <Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">첨부 파일</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadAll(detail)}
                    >
                      모두 받기 ({detail.attachments.length})
                    </Button>
                  </Stack>
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {detail.attachments.map((a) => (
                      <Button
                        key={a.id}
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => handleDownload(a.id, a.original_name)}
                      >
                        {a.original_name}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail?.can_edit && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => {
                setDetailOpen(false);
                openEdit(detail);
              }}
            >
              수정
            </Button>
          )}
          {detail?.can_delete && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => removePost(detail)}>
              삭제
            </Button>
          )}
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Paper sx={{ p: 2 }} elevation={1}>
      {content}
    </Paper>
  );
};

export default ControlArchivePanel;
